import { ChatMap, NOOP, } from "@shared";
import { writable } from "../../utils/stores";
import { LocalMap } from "../map";
import { LocalSet } from "../set";
import {} from "../undo";
import { modifyWritable, notEq } from "../utils";
const localMap = () => new LocalMap();
const localSet = () => new LocalSet();
export class ChatDetailsUpdatesManager {
    members = writable(new ChatMap(), undefined, notEq);
    blockedUsers = writable(new ChatMap(), undefined, notEq);
    lapsed = writable(new ChatMap(), undefined, notEq);
    pinnedMessages = writable(new ChatMap(), undefined, notEq);
    invitedUsers = writable(new ChatMap(), undefined, notEq);
    bots = writable(new ChatMap(), undefined, notEq);
    webhooks = writable(new ChatMap(), undefined, notEq);
    rules = writable(new ChatMap(), undefined, notEq);
    pinnedToScopes = writable(new ChatMap(), undefined, notEq);
    #updateForChat(id, store, notFound, updater) {
        return modifyWritable((chatMap) => {
            let val = chatMap.get(id);
            if (val === undefined) {
                val = notFound();
                chatMap.set(id, val);
            }
            return updater(val);
        }, store);
    }
    updateMember(id, userId, existing, updater) {
        if (existing !== undefined) {
            this.#updateForChat(id, this.members, localMap, (map) => map.addOrUpdate(userId, updater(existing)));
        }
        return NOOP;
    }
    blockUser(id, userId) {
        return this.#updateForChat(id, this.blockedUsers, localSet, (set) => set.add(userId));
    }
    unblockUser(id, userId) {
        return this.#updateForChat(id, this.blockedUsers, localSet, (set) => set.remove(userId));
    }
    removeMember(id, userId) {
        const undoMembers = this.#updateForChat(id, this.members, localMap, (map) => map.remove(userId));
        const undoLapsed = this.#updateForChat(id, this.lapsed, localSet, (set) => set.remove(userId));
        return () => {
            undoLapsed();
            undoMembers();
        };
    }
    addMember(id, member) {
        return this.#updateForChat(id, this.members, localMap, (map) => map.addOrUpdate(member.userId, member));
    }
    pinToScope(id, scope) {
        return this.#updateForChat(id, this.pinnedToScopes, localSet, (set) => set.add(scope));
    }
    unpinFromScope(id, scope) {
        return this.#updateForChat(id, this.pinnedToScopes, localSet, (set) => set.remove(scope));
    }
    pinMessage(id, messageIndex) {
        return this.#updateForChat(id, this.pinnedMessages, localSet, (set) => set.add(messageIndex));
    }
    unpinMessage(id, messageIndex) {
        return this.#updateForChat(id, this.pinnedMessages, localSet, (set) => set.remove(messageIndex));
    }
    inviteUsers(id, userIds) {
        return this.#updateForChat(id, this.invitedUsers, localSet, (set) => {
            const undos = userIds.map((u) => set.add(u));
            return () => {
                undos.forEach((u) => u());
            };
        });
    }
    uninviteUsers(id, userIds) {
        return this.#updateForChat(id, this.invitedUsers, localSet, (set) => {
            const undos = userIds.map((u) => set.remove(u));
            return () => {
                undos.forEach((u) => u());
            };
        });
    }
    updateRules(id, rules) {
        return modifyWritable((map) => {
            const prev = map.get(id);
            map.set(id, rules);
            return () => {
                if (prev !== undefined) {
                    map.set(id, prev);
                }
                else {
                    map.delete(id);
                }
            };
        }, this.rules);
    }
    removeBot(id, botId) {
        return this.#updateForChat(id, this.bots, localMap, (map) => map.remove(botId));
    }
    installBot(id, botId, perm) {
        return this.#updateForChat(id, this.bots, localMap, (map) => map.addOrUpdate(botId, perm));
    }
    addWebhook(id, webhook) {
        return this.#updateForChat(id, this.webhooks, localMap, (map) => map.addOrUpdate(webhook.id, webhook));
    }
    updateWebhook(id, webhook) {
        return this.#updateForChat(id, this.webhooks, localMap, (map) => map.addOrUpdate(webhook.id, webhook));
    }
    removeWebhook(id, webhookId) {
        return this.#updateForChat(id, this.webhooks, localMap, (map) => map.remove(webhookId));
    }
    // Only used for testing
    clearAll() {
        this.pinnedToScopes.set(new ChatMap());
        this.pinnedMessages.set(new ChatMap());
        this.invitedUsers.set(new ChatMap());
        this.blockedUsers.set(new ChatMap());
        this.members.set(new ChatMap());
        this.bots.set(new ChatMap());
        this.webhooks.set(new ChatMap());
        this.rules.set(new ChatMap());
    }
}
export const chatDetailsLocalUpdates = new ChatDetailsUpdatesManager();
